'use client';

/**
 * ChargeAppleMerchant Component
 *
 * Headless processor for Apple Merchant (recurring Apple Pay) payments.
 * Unlike regular Apple Pay, Apple Merchant has the token already saved,
 * so we can charge directly without showing the Apple Pay sheet.
 * Does NOT render any UI - parent uses ChargeStatusDisplay for display.
 *
 * Flow:
 * 1. Get processing data from /process_credits
 * 2. Create purchase via /user_credits (with device_session_id)
 * 3. Poll for result
 */

import React, { useEffect, useRef } from 'react';
import { usePurchaseLog } from '../../config/PurchaseFlowConfigProvider';
import { getClientPlatformForAPI, browserContext } from '../../utils/platform';
import {
  getProcessingData,
  createPurchase,
  getPurchaseStatus,
  ApiError
} from '../../api';
import { poll } from '../../utils/polling';
import type { ChargeProcessorProps } from '../../types';

export const ChargeAppleMerchant: React.FC<ChargeProcessorProps> = ({
  amount,
  withBonus = false,
  paymentSourceId,
  purchaseType,
  advisorId,
  clickSource,
  purchaseContext,
  fxRate,
  currency,
  riskModel,
  riskModelConfig,
  onStatusChange,
  onSuccess,
  onError,
  startSignal = 0
}) => {
  const completedRef = useRef(false);
  const log = usePurchaseLog();

  useEffect(() => {
    if (!startSignal) return undefined;
    completedRef.current = false;

    const processPayment = async () => {
      try {
        onStatusChange?.('processing', 'Loading payment configuration...');
        log('info', 'Apple Merchant', 'Starting payment', { amount, paymentSourceId });

        const processingData = await getProcessingData(amount, {
          use_reference_transaction: true,
          with3ds: true,
          with_bonus: withBonus,
          purchase_type: purchaseType?.toLowerCase(),
          context: purchaseContext,
          platform: getClientPlatformForAPI(),
          ...browserContext,
          ...(advisorId && { intended_advisor_id: advisorId }),
          ...(clickSource && { click_source: clickSource }),
          ...(currency && { currency }),
          ...(fxRate && { fx_rate: fxRate })
        });

        if (completedRef.current) return;

        onStatusChange?.('processing', 'Processing payment...');
        const purchaseResponse = await createPurchase({
          requiredParams: processingData.required_params || {},
          riskModel,
          riskModelConfig,
          fxRate,
          currency
        });

        if (purchaseResponse.error) {
          log('error', 'Apple Merchant', 'Create purchase error', {
            errorMessage: purchaseResponse.error,
            errorType: purchaseResponse.errorType
          });
          onError({
            success: false,
            errorMessage: purchaseResponse.error,
            errorType: purchaseResponse.errorType,
            errorTitle: purchaseResponse.errorTitle,
            recoverable: purchaseResponse.recoverable,
            retriable: purchaseResponse.retriable,
            errorOrigin: purchaseResponse.errorOrigin,
            errorPathname: purchaseResponse.errorPathname,
            spendingLimits: purchaseResponse.spendingLimits
          });
          return;
        }

        if (!purchaseResponse.id) {
          onError({ success: false });
          return;
        }

        if (completedRef.current) return;

        onStatusChange?.('polling', 'Completing payment...');
        const response = await poll({
          pollFunction: () => getPurchaseStatus(
            purchaseResponse.id,
            getClientPlatformForAPI()
          ),
          checkComplete: (res) => !!(res.okMessage || res.errorMessage)
        });

        if (completedRef.current) return;

        if (response.errorMessage) {
          log('error', 'Apple Merchant', 'Apple Merchant charge processing error (poll)', {
            errorMessage: response.errorMessage,
            errorType: response.errorType
          });
          onError({
            success: false,
            errorMessage: response.errorMessage,
            errorType: response.errorType,
            errorTitle: response.errorTitle,
            recoverable: response.recoverable,
            retriable: response.retriable,
            errorOrigin: response.errorOrigin,
            errorPathname: response.errorPathname,
            spendingLimits: response.spendingLimits
          });
          return;
        }

        onSuccess({
          success: true,
          userCreditId: purchaseResponse.id,
          okMessage: response.okMessage,
          cashbackAmount: response.cashbackAmount,
          pathname: response.pathname,
          threeDAuthResult: response.threeDAuthResult
        });
      } catch (error) {
        if (completedRef.current) return;
        log('error', 'Apple Merchant', 'Payment failed', { error });
        onError({ success: false, errorMessage: error instanceof ApiError ? error.userMessage : undefined });
      }
    };

    processPayment();
    return () => { completedRef.current = true; };
  }, [startSignal, log]);

  // Headless component - no UI rendered
  return null;
};
